/**
 * RolePill — two-segment pill above the search bar.
 *
 * Lets the user declare whether they're checking a property to buy, or
 * preparing one they own. Per the UX research locked on 2026-05-29:
 *
 *   - "Buying" is the default. Most traffic is pre-offer buyers.
 *   - "I own this property" uses ownership language, not transaction-
 *     stage language ("seller" presumes a decision the user hasn't
 *     necessarily made).
 *   - State lives in a cookie (see `useRole`), NOT in the URL, so
 *     /property/:id stays canonical for SEO.
 *
 * Visually a small gov.uk-form-grey pill — austere, not marketing-warm.
 */

import { Role } from '../hooks/useRole';

interface RolePillProps {
  role: Role;
  onChange: (next: Role) => void;
  className?: string;
}

const SEGMENTS: Array<{ value: Role; label: string }> = [
  { value: 'buyer', label: "I'm buying" },
  { value: 'owner', label: 'I own this property' },
];

export default function RolePill({ role, onChange, className = '' }: RolePillProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Are you buying or do you own this property?"
      className={`inline-flex items-center rounded-full border border-slate-300 bg-white p-1 text-sm ${className}`}
    >
      {SEGMENTS.map((seg) => {
        const selected = role === seg.value;
        return (
          <button
            type="button"
            key={seg.value}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(seg.value)}
            className={`px-4 py-1.5 rounded-full font-medium transition-colors min-h-[36px] ${
              selected
                ? 'bg-emerald-700 text-white'
                : 'text-slate-700 hover:text-slate-950'
            }`}
          >
            {seg.label}
          </button>
        );
      })}
    </div>
  );
}
